/**
 * A guest's own two flights, and where each one is (D71).
 *
 * The flight numbers are seeded beside the dates on the roster row
 * (`arrivalFlight`, `departureFlight`), so a guest never types one. The live
 * half comes from FlightAware's AeroAPI, read by the server on a schedule set
 * by how close the flight is, cached per flight and shared by everyone on it,
 * and capped per month — see `services/media/src/flights.ts`.
 */

/** One end of a flight: when it was meant to happen, the airline's current
 *  guess, and when it did. ISO 8601 instants, plus the airport's IANA zone so
 *  the client can print the time the guest will see on the board. */
export interface FlightTimes {
  scheduled?: string;
  estimated?: string;
  actual?: string;
  /** The airport's IATA code where it has one, ICAO where it does not. */
  airport?: string;
  tz?: string;
}

/**
 * Where a flight is, reduced to the five things a guest acts on.
 *
 * `en-route` covers everything between pushback and the gate, because the
 * difference between taxiing and cruising is not something anyone waiting at
 * Phuket needs.
 */
export type FlightPhase = 'scheduled' | 'en-route' | 'landed' | 'cancelled' | 'diverted';

export interface FlightStatus {
  /** As printed: `TG 225`, carrier and number apart. */
  flight: string;
  /** The FlightAware page for this flight. Always present, so a guest can
   *  look for themselves whether or not live status is available. */
  trackUrl: string;
  /**
   * Whether the fields below came from tracking.
   *
   * False before a flight is close enough to look up (AeroAPI has nothing
   * more than two days out), when the roster date cannot be read, and when
   * tracking is switched off or out of budget. The client shows the flight
   * number and the link, and nothing that would read as a status.
   */
  live: boolean;
  phase?: FlightPhase;
  departs?: FlightTimes;
  lands?: FlightTimes;
  /** Minutes late against the timetable, at whichever end matters for this
   *  direction. Absent when on time or unknown; never negative. */
  delayMinutes?: number;
  /** When the server last asked FlightAware, epoch ms. */
  checkedAt?: number;
}

/** `GET /me/flights`. Each key is absent when the roster has no flight
 *  number for that direction. */
export interface FlightsResponse {
  arrival?: FlightStatus;
  departure?: FlightStatus;
}

/**
 * `TG225`, `tg 225`, `TG-225` → `TG 225`. Undefined for anything that is not
 * a flight number, so a note typed into the column ("driving from Phuket")
 * shows as written and is never looked up.
 *
 * Carrier codes are two characters (IATA, letters and digits: `3K`, `9W`) or
 * three letters (ICAO: `THA`). Numbers are one to four digits with an optional
 * suffix letter.
 */
export function normaliseFlight(text: string | undefined): string | undefined {
  if (!text) return undefined;
  const m = /^([A-Z][A-Z0-9]|[0-9][A-Z]|[A-Z]{3})[\s-]?(\d{1,4}[A-Z]?)$/.exec(text.trim().toUpperCase());
  if (!m) return undefined;
  return `${m[1]!} ${m[2]!.replace(/^0+(?=\d)/, '')}`;
}

/**
 * Airlines' two-character IATA codes → their three-letter ICAO codes.
 *
 * **FlightAware only knows a flight by its ICAO form.** `/live/flight/TG203`
 * is FlightAware's "Unknown Flight" page and `/live/flight/THA203` is the
 * flight. Tested 22 Oct 2026 for every airline on the roster: all fourteen
 * failed as IATA and worked as ICAO, bar Korean Air, which worked both ways.
 * Every link the site had printed was the broken form.
 *
 * Every airline on the roster, plus the ones a guest flying into Phuket via
 * Bangkok, Singapore, Kuala Lumpur, Hong Kong or the Gulf is likely to add.
 * An airline missing from here keeps its IATA form, which is what every
 * flight got before; add it rather than guess.
 */
export const ICAO_AIRLINE: Readonly<Record<string, string>> = {
  // On the roster, 22 Oct 2026.
  QP: 'AKJ', SQ: 'SIA', TG: 'THA', '6E': 'IGO', FD: 'AIQ', CX: 'CPA', BR: 'EVA',
  EY: 'ETD', PG: 'BKP', KE: 'KAL', TK: 'THY', MH: 'MAS', DL: 'DAL', AI: 'AIC',
  // Likely additions.
  AK: 'AXM', TR: 'TGW', '3K': 'JSA', SL: 'TLM', WE: 'THD', VZ: 'TVJ', IX: 'AXB',
  UK: 'VTI', UL: 'ALK', EK: 'UAE', QR: 'QTR', VN: 'HVN', GA: 'GIA', JL: 'JAL',
  NH: 'ANA', OZ: 'AAR', CA: 'CCA', MU: 'CES', CZ: 'CSN', QF: 'QFA', LH: 'DLH',
  BA: 'BAW', AF: 'AFR', KL: 'KLM', UA: 'UAL', AA: 'AAL',
};

/**
 * A flight as FlightAware names it: `TG 203` → `THA203`. A flight already
 * written with an ICAO code, or on an airline the table does not have, is
 * passed through with its spaces removed.
 */
export function flightIdent(flight: string): string {
  const m = /^([A-Z0-9]{2})\s*(\d{1,4}[A-Z]?)$/.exec(flight.trim().toUpperCase());
  const icao = m && ICAO_AIRLINE[m[1]!];
  return icao ? `${icao}${m![2]}` : flight.replace(/\s+/g, '');
}

/** The FlightAware page for a flight, from its printed form. */
export const flightTrackUrl = (flight: string) =>
  `https://www.flightaware.com/live/flight/${encodeURIComponent(flightIdent(flight))}`;
